import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { setTemplateTranslations } from '@focuslee/shared-mini-app';
import { AppRoot } from '@telegram-apps/telegram-ui';
import { cloudStorage, initData, useSignal } from '@tma.js/sdk-react';
import { AnimatePresence } from 'framer-motion';
import { isMockedEnvironment } from 'helpers/telegram';
import { useEffect } from 'react';
import { HashRouter, matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AnalyticsProvider } from '../contexts/AnalyticsContext';
import { FeatureFlagProvider } from '../contexts/FeatureFlagContext';
import { MockBackButton } from './MockBackButton';

import {
    achievementCategoryPath,
    achievementCreatePath,
    achievementEditPath,
    achievementsTabPath,
    achievementViewPath,
    activityPath,
    categoriesListTabPath,
    categoryCreatePath,
    categoryEditPath,
    categoryPath,
    completedAchievementsPath,
    focusModePath,
    goalsCompletedPath,
    goalsConfigurePath,
    goalsSelectPath,
    goalsViewPath,
    journalEntryPath,
    languagePath,
    journalTabPath,
    oauthApprovalPath,
    onboardingCategorySelectionPath,
    onboardingWelcomePath,
    postponeEditPath,
    premiumPath,
    profileTabPath,
    questCreatePath,
    questEditPath,
    questTemplateConfigPath,
    questTemplatesPath,
    questViewPath,
    ratingEntryPath,
    rewardCreatePath,
    rewardEditPath,
    rewardsPath,
    rewardViewPath,
    ritualEditPath,
    ritualTaskEditPath,
    ritualViewPath,
    rootPath,
    todayQuestProgressPath,
    voiceCommandsDocsPath,
} from 'navigation/paths';
import { AchievementCreateEditPage } from 'pages/AchievementCreateEditPage';
import { AchievementPage } from 'pages/AchievementPage';
import { AchievementViewPage } from 'pages/AchievementViewPage';
import { ActivityPage } from 'pages/ActivityPage';
import { CategoryCreateEditPage } from 'pages/CategoryCreateEditPage';
import { CategoryQuestListPage } from 'pages/CategoryQuestListPage';
import { CompletedAchievementsPage } from 'pages/CompletedAchievementsPage';
import { FocusModePage } from 'pages/FocusModePage';
import { GoalsConfigurePage } from 'pages/GoalsConfigurePage';
import { GoalsSelectPage } from 'pages/GoalsSelectPage';
import { GoalsViewPage } from 'pages/GoalsViewPage';
import { JournalEntryPage } from 'pages/JournalEntryPage';
import { LanguagePage } from 'pages/LanguagePage';
import { JournalPage } from 'pages/JournalPage';
import { OnBoardingWelcomePage } from 'pages/onboarding/OnBoardingWelcomePage';
import { PostponeEditPage } from 'pages/PostponeEditPage';
import { PremiumPage } from 'pages/PremiumPage';
import { ProfilePage } from 'pages/ProfilePage';
import { QuestCreateEditPage } from 'pages/QuestCreateEditPage';
import { QuestListPage } from 'pages/QuestListPage';
import { RatingEntryPage } from 'pages/RatingEntryPage';
import { RewardCreateEditPage } from 'pages/RewardCreateEditPage';
import { RewardsPage } from 'pages/RewardsPage';
import { RewardViewPage } from 'pages/RewardViewPage';
import { RitualEditPage } from 'pages/RitualEditPage';
import { RitualPage } from 'pages/RitualPage';
import { RitualTaskEditPage } from 'pages/RitualTaskEditPage';
import { TemplateConfigPage } from 'pages/TemplateConfigPage';
import { TemplatesPage } from 'pages/TemplatesPage';
import { TodayQuestProgressPage } from 'pages/TodayQuestProgressPage';
import { VoiceCommandsDocsPage } from 'pages/VoiceCommandsDocsPage';
import { OAuthApprovalPage } from '../pages/OAuthApprovalPage';
import { loginData } from '../client/apollo';
import { LoginMutation, LoginMutationVariables } from '../client/generated/graphql';
import { GetTemplatesQuery } from '../client/generated/graphql';
import { loginMutation } from '../client/mutations/login';
import { getTemplatesQuery } from '../client/queries/template';
import { useAppearance } from '../hooks/useAppearance';
import { AchievementListPage } from '../pages/AchievementListPage';
import { CategoriesPage } from '../pages/CategoriesPage';
import { GoalsCompletePage } from '../pages/GoalsCompletePage';
import { OnBoardingCategorySelectionPage } from '../pages/onboarding/OnBoardingCategorySelectionPage';
import { AppRouter } from './AppRouter';
import { QuestCompletionHandler } from './quest/QuestCompletionHandler';
import { TabLayout } from './TabLayout';
import { TimerRestoreHandler } from './TimerRestoreHandler';
import { VoiceProvider } from '../contexts/VoiceContext';
import { useLanguageSync } from '../i18n/useLanguageSync';

export function App() {
    const languageReady = useLanguageSync();
    const appearance = useAppearance();
    const initDataRaw = useSignal(initData.raw);
    const [login, { loading }] = useMutation<LoginMutation, LoginMutationVariables>(loginMutation);
    const authData = useReactiveVar(loginData);
    const { data: templatesData } = useQuery<GetTemplatesQuery>(getTemplatesQuery, { skip: !authData, fetchPolicy: 'cache-first' });
    if (templatesData) setTemplateTranslations(templatesData.templateCategories);

    useEffect(() => {
        if (initDataRaw) {
            login({
                variables: { initDataRaw },
                onCompleted: async res => {
                    const data = res.login!;
                    loginData(data);
                    if (!isMockedEnvironment() && cloudStorage.isSupported()) {
                        await cloudStorage.setItem('login_data', JSON.stringify(data));
                    }
                },
            });
        }
    }, [initDataRaw]);

    // Don't render anything until we have login data to prevent queries from running during onboarding
    if (loading || !authData || !languageReady) return <></>;

    return (
        <AppRoot
            appearance={appearance}
            platform="ios"
            style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        >
            {isMockedEnvironment() && <MockBackButton />}
            <HashRouter>
                <AnalyticsProvider>
                    <FeatureFlagProvider>
                        <VoiceProvider>
                            <AppRouter />
                            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                <AnimatedRoutes />
                            </div>
                            <TimerRestoreHandler />
                            <QuestCompletionHandler />
                        </VoiceProvider>
                    </FeatureFlagProvider>
                </AnalyticsProvider>
            </HashRouter>
        </AppRoot>
    );
}

// AnimatedRoutes wraps our app's Routes in AnimatePresence (initial animations disabled) to enable exit transitions
function AnimatedRoutes() {
    const location = useLocation();
    const { pathname } = location;
    const shouldAnimate =
        matchPath(questViewPath, pathname) ||
        matchPath(rewardViewPath, pathname) ||
        matchPath(achievementViewPath, pathname) ||
        matchPath(focusModePath, pathname);
    const routesElement = (
        <Routes key={pathname} location={shouldAnimate ? location : undefined}>
            <Route element={<TabLayout />}>
                <Route index element={<QuestListPage />} />
                <Route path={achievementsTabPath} element={<AchievementPage />} />
                <Route path={categoriesListTabPath} element={<CategoriesPage />} />
                <Route path={journalTabPath} element={<JournalPage />} />
                <Route path={profileTabPath} element={<ProfilePage />} />
            </Route>
            <Route path={onboardingWelcomePath} element={<OnBoardingWelcomePage />} />
            <Route path={onboardingCategorySelectionPath} element={<OnBoardingCategorySelectionPage />} />
            <Route path={achievementCategoryPath} element={<AchievementListPage />} />
            <Route path={achievementViewPath} element={<AchievementViewPage />} />
            <Route path={achievementCreatePath} element={<AchievementCreateEditPage />} />
            <Route path={achievementEditPath} element={<AchievementCreateEditPage />} />
            <Route path={categoryPath} element={<CategoryQuestListPage />} />
            <Route path={categoryCreatePath} element={<CategoryCreateEditPage />} />
            <Route path={categoryEditPath} element={<CategoryCreateEditPage />} />
            <Route path={questCreatePath} element={<QuestCreateEditPage />} />
            <Route path={questEditPath} element={<QuestCreateEditPage />} />
            <Route path={postponeEditPath} element={<PostponeEditPage />} />
            <Route path={questViewPath} element={<QuestCreateEditPage />} />
            <Route path={rewardViewPath} element={<RewardViewPage />} />
            <Route path={goalsCompletedPath} element={<GoalsCompletePage />} />
            <Route path={goalsViewPath} element={<GoalsViewPage />} />
            <Route path={goalsConfigurePath} element={<GoalsConfigurePage />} />
            <Route path={goalsSelectPath} element={<GoalsSelectPage />} />
            <Route path={completedAchievementsPath} element={<CompletedAchievementsPage />} />
            <Route path={activityPath} element={<ActivityPage />} />
            <Route path={focusModePath} element={<FocusModePage />} />
            <Route path={rewardsPath} element={<RewardsPage />} />
            <Route path={rewardCreatePath} element={<RewardCreateEditPage />} />
            <Route path={rewardEditPath} element={<RewardCreateEditPage />} />
            <Route path={questTemplatesPath} element={<TemplatesPage />} />
            <Route path={questTemplateConfigPath} element={<TemplateConfigPage />} />
            <Route path={todayQuestProgressPath} element={<TodayQuestProgressPage />} />
            <Route path={ritualViewPath} element={<RitualPage />} />
            <Route path={ritualEditPath} element={<RitualEditPage />} />
            <Route path={ritualTaskEditPath} element={<RitualTaskEditPage />} />
            <Route path={journalEntryPath} element={<JournalEntryPage />} />
            <Route path={ratingEntryPath} element={<RatingEntryPage />} />
            <Route path={oauthApprovalPath} element={<OAuthApprovalPage />} />
            <Route path={voiceCommandsDocsPath} element={<VoiceCommandsDocsPage />} />
            <Route path={premiumPath} element={<PremiumPage />} />
            <Route path={languagePath} element={<LanguagePage />} />
            <Route path="*" element={<Navigate to={rootPath} />} />
        </Routes>
    );
    return shouldAnimate ? <AnimatePresence mode="wait">{routesElement}</AnimatePresence> : routesElement;
}
